import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useRelatedShows from '../../hooks/useRelatedShows';
import useShowUrlParams from '../../../../../../hooks/shows/useShowUrlParams';
import { Provider } from './context';
import { size } from '@/helpers/lodash';
import useShow from '@/hooks/shows/useShowFromURL';
import useAudioReduxControlsCustom from '@/hooks/audio/useAudioReduxControlsCustom';
import useCurrentlyPlayingShow from '@/hooks/shows/useCurrentlyPlayingShow';
import useRowImpressionEvent from '@/hooks/events/useRowImpressionEvent';
import sendChannelViewEvent from '@/helpers/api/events/sendChannelViewEvent';
import useInitialRender from '@/hooks/browser/useInitialRender';

const useRelatedShowRow = () => {
  const { shows: relatedShows } = useRelatedShows();
  const { show } = useShow();
  const { id: showId } = show;

  // Send an impression event for the related channels row
  const { relatedRowImpressionRef } = useRowImpressionEvent({
    shouldCall: !!size(relatedShows),
    recommendationId: showId,
    recommendationType: 'SEED'
  });

  const onClickRelatedShow = useCallback(
    (channelClicked) =>
      sendChannelViewEvent({
        channelId: channelClicked.id,
        recommendationType: 'SEED',
        recommendationId: channelClicked.id
      }).catch(_ => _),
    []
  );

  return {
    relatedRowImpressionRef,
    onClickRelatedShow
  };
};

const useEpisodeAutoScroll = () => {
  const { isFirstRender } = useInitialRender();

  // A reference to the episodes container so that we scroll within the episodes div
  // When visiting an episode page
  const episodeScrollContainerRef = useRef(null);

  // When the user arrives on an episode page, we scroll to the episode.
  // This flag marks whether we have scrolled to the episode or not
  const [autoScrollComplete, setAutoScrollComplete] = useState(false);

  const onAutoScrollComplete = useCallback(() => setAutoScrollComplete(true), []);

  return {
    // This ensures the ref is not attached to both desktop and mobile containers at once
    episodeScrollContainerRef: isFirstRender ? null : episodeScrollContainerRef,
    autoScrollComplete,
    onAutoScrollComplete
  };
};

const useSeasons = () => {
  const { show } = useShow();
  const { seasons } = show;
  const { seasonNumber: seasonNumberInRoute } = useShowUrlParams();

  const [currentSeasonNumber, setCurrentSeasonNumber] = useState();

  // Handle selecting a season
  const onChangeSeason = useCallback((newSeasonNumber) => {
    const newSeasonNumberString = newSeasonNumber.toString();

    setCurrentSeasonNumber(newSeasonNumberString);
  }, []);

  const setInitialSeason = useCallback(() => {
    if (show && seasons) {
      if (seasonNumberInRoute) {
        if (seasons.includes(seasonNumberInRoute.toString())) {
          onChangeSeason(seasonNumberInRoute);
        } else {
          onChangeSeason(seasons[0]);
        }
      } else if (!seasons.includes(currentSeasonNumber)) {
        onChangeSeason(seasons[0]);
      }
    }
  }, [currentSeasonNumber, seasonNumberInRoute, show, onChangeSeason, seasons]);

  useEffect(() => {
    if (!currentSeasonNumber) {
      setInitialSeason();
    }
  }, [setInitialSeason, currentSeasonNumber]);

  // Calculate options for seasons
  const seasonOptions = useMemo(() => (seasons || []).map((s) => ({
    id: s,
    value: `Season ${s}`
  })), [seasons]);

  return {
    currentSeasonNumber,
    onChangeSeason,
    setInitialSeason,
    seasonOptions,
    haveSetSeasonFromUrl: !!currentSeasonNumber
  };
};

const ShowPageProvider = ({ children }) => {
  const currentlyPlayingChannel = useCurrentlyPlayingShow();
  const { show } = useShow();

  const { id: showId } = show;
  const isListeningToThisShow = (currentlyPlayingChannel || {}).id === showId;
  const {
    relatedRowImpressionRef,
    onClickRelatedShow
  } = useRelatedShowRow();
  const {
    episodeScrollContainerRef,
    autoScrollComplete,
    onAutoScrollComplete
  } = useEpisodeAutoScroll();
  const {
    currentSeasonNumber,
    onChangeSeason,
    setInitialSeason,
    seasonOptions,
    haveSetSeasonFromUrl
  } = useSeasons();
  const { handlePlayEpisodeFromShow: onClickPlayEpisode } = useAudioReduxControlsCustom();

  const value = useMemo(() => ({
    // Common
    onClickPlayEpisode,
    isListeningToThisShow,
    // Episodes
    episodeScrollContainerRef,
    autoScrollComplete,
    onScrolledToEpisode: onAutoScrollComplete,
    shouldScrollToEpisode: !autoScrollComplete,
    // Related shows
    relatedRowImpressionRef,
    onClickRelatedShow,
    // Seasons
    currentSeasonNumber,
    haveSetSeasonFromUrl,
    onChangeSeason,
    setInitialSeason,
    seasonOptions
  }), [
    onClickPlayEpisode,
    isListeningToThisShow,
    episodeScrollContainerRef,
    autoScrollComplete,
    onAutoScrollComplete,
    relatedRowImpressionRef,
    onClickRelatedShow,
    currentSeasonNumber,
    haveSetSeasonFromUrl,
    onChangeSeason,
    setInitialSeason,
    seasonOptions
  ]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

ShowPageProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default ShowPageProvider;
